import { flexRender, type Header } from '@tanstack/react-table'
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react'
import { TableHead } from '~/shadcn/ui/table'
import { cn } from '~/shadcn/lib/utils'
import type { ProjectsItem } from '../../_project_types/project.types'

type Props = {
  header: Header<ProjectsItem, unknown>
  index: number
  /** Pridať text-primary-foreground a justify-start do inner div */
  usePrimaryForeground?: boolean
  /** Použiť custom farby pre ikony namiesto theme colors */
  useCustomIconColors?: boolean
  colorScheme?: 'dark' | 'light'
}

export function ProjectsTableHead({
  header,
  index,
  usePrimaryForeground = true,
  useCustomIconColors = true,
  colorScheme = 'dark',
}: Props) {
  if (header.isPlaceholder) {
    return <TableHead key={`TableHead-${header.id}`} />
  }

  const isDark = colorScheme === 'dark'
  const canSort = header.column.getCanSort()
  const sortingOrder = header.column.getNextSortingOrder()
  const isSorted = header.column.getIsSorted()

  const orderByText = {
    asc: 'Zoradiť vzostupne',
    desc: 'Zoradiť zostupne',
  }

  const title = canSort
    ? sortingOrder
      ? orderByText[sortingOrder]
      : 'Zrušiť triedenie'
    : undefined

  const sortingIcon = {
    asc: <ArrowUpIcon className="h-4 w-4" />,
    desc: <ArrowDownIcon className="h-4 w-4" />,
  }[isSorted || 'desc']

  // TableHead className: include header.id alebo prázdny string
  const tableHeadClassName = cn(
    index === 0 && `border-r`,
    isDark ? 'border-border-strong' : 'border-border',
    header.id
  )

  // Inner div className: pridaj text-primary-foreground a justify-start ak je potrebné
  const innerDivClassName = cn(
    'flex items-center',
    usePrimaryForeground && 'text-primary-foreground justify-start',
    canSort && 'cursor-pointer'
  )

  // Icon colors: theme colors alebo custom colors
  const iconClassName = cn(
    'ml-2',
    useCustomIconColors
      ? isSorted
        ? 'text-[#222]'
        : 'text-[#E3E3E3]'
      : isSorted
        ? 'text-primary-foreground'
        : 'text-muted-foreground'
  )

  return (
    <TableHead key={`TableHead-${header.id}`} className={tableHeadClassName}>
      <div
        className={innerDivClassName}
        title={title}
        onClick={header.column.getToggleSortingHandler()}>
        {flexRender(header.column.columnDef.header, header.getContext())}
        {canSort && <span className={iconClassName}>{sortingIcon}</span>}
      </div>
    </TableHead>
  )
}
